import { FLAG_CONSTANTS } from '@/constants'
import flagsmith from 'flagsmith'
import { Settings } from '@/Settings'
import * as Sentry from '@sentry/vue'

import store from '@/store'

// Mutation types
export const UPDATE_FLAG = 'updateFlag'
export const SET_USER_ID = 'setUserId'
export const SET_USER_TRAITS = 'setUserTraits'
export const SET_USER_SEGMENT = 'setUserSegment'

// Action types
export const IDENTIFY_USER = 'identifyUser'
export const REFRESH_FLAGS = 'refreshFlags'
export const INITIALIZE_FLAGSMITH = 'initializeFlagsmith'

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

function reportToSentry(message, level = 'error') {
  if (Settings.getConfig('VUE_APP_SENTRY_DSN')) {
    Sentry.captureMessage(message, level)
  }
}

export const syncFlags = newFlags => {
  Object.values(FLAG_CONSTANTS).forEach(flagKey => {
    if (hasOwn(newFlags, flagKey)) {
      const enabled = newFlags[flagKey]?.enabled || false
      store.commit(`flags/${UPDATE_FLAG}`, {
        flag: flagKey,
        value: enabled,
      })
    }
  })
}

export const state = {
  [FLAG_CONSTANTS.RETAILER_AI]: false,
  [FLAG_CONSTANTS.DOWNLOAD_COLLECTION_WITH_ERRORS]: false,
  [FLAG_CONSTANTS.REGISTRATION_FLOW_V2]: false,
  [FLAG_CONSTANTS.NOTIFICATION_PREFERENCES]: false,
  [FLAG_CONSTANTS.KP_2682_MODIFY_ORDER]: false,
  [FLAG_CONSTANTS.KP_2629_REMOVE_B2B_AUTH]: false,
  [FLAG_CONSTANTS.UNIFIED_INVITE_RETAILER]: false,
  [FLAG_CONSTANTS.KP_3080_INTERNAL_ANALYTICS]: false,
  [FLAG_CONSTANTS.PUBLIC_WISHLIST_V2]: false,
  [FLAG_CONSTANTS.KP_3462_COLLAPSIBLE_SIDEBAR]: false,
  [FLAG_CONSTANTS.KP_4128_BULK_REMOVE_SHIPMENT_PRODUCTS]: false,
  [FLAG_CONSTANTS.KP_3919_BRAND_CATALOGUE_FILES]: false,
  userId: null,
  userTraits: {},
  userSegment: null,
}

export const getters = {
  hasFeature: state => flagName => state[flagName] || false,
  isDemo: state => state.userSegment === 'demo',
  isRegularUser: state => state.userSegment === 'regular',
  allFlags: state => {
    const flagsArray = []

    flagsArray.push({
      name: FLAG_CONSTANTS.RETAILER_AI,
      enabled: state[FLAG_CONSTANTS.RETAILER_AI],
      value: flagsmith.getValue(FLAG_CONSTANTS.RETAILER_AI),
    })

    // Add any other flags from Flagsmith
    const flagsmithFlags = flagsmith.getAllFlags()
    if (flagsmithFlags) {
      Object.keys(flagsmithFlags).forEach(key => {
        if (key !== FLAG_CONSTANTS.RETAILER_AI) {
          flagsArray.push({
            name: key,
            enabled: flagsmithFlags[key].enabled,
            value: flagsmithFlags[key].value,
          })
        }
      })
    }

    return flagsArray
  },
}

export const mutations = {
  [UPDATE_FLAG](state, payload) {
    state[payload.flag] = payload.value
  },
  [SET_USER_ID](state, userId) {
    state.userId = userId
  },
  [SET_USER_TRAITS](state, traits) {
    state.userTraits = traits
  },
  [SET_USER_SEGMENT](state, segment) {
    state.userSegment = segment
  },
}

export const actions = {
  async [INITIALIZE_FLAGSMITH]({ dispatch }) {
    const environmentID = Settings.getConfig('FLAGSMITH_ENVIRONMENT_ID')
    if (environmentID) {
      await flagsmith.init({
        environmentID,
        cacheFlags: true,
        onChange: newFlags => syncFlags(newFlags),
      })
      await dispatch(REFRESH_FLAGS)
    } else {
      const errorMessage = 'Flagsmith Error: Environment ID is missing. Flagsmith will not be initialized.'
      console.error(errorMessage)
      reportToSentry(errorMessage, 'error')
    }
  },
  async [IDENTIFY_USER]({ commit }, { id, traits }) {
    if (!id) {
      const errorMessage = 'Flagsmith: Attempted to identify user with MISSING ID.'
      console.error(errorMessage)
      reportToSentry(errorMessage, 'error')
      return
    }

    commit(SET_USER_ID, id)
    commit(SET_USER_TRAITS, traits)
    flagsmith.identify(id.toString(), traits)

    const isDemo = traits?.isDemo
    const segment = isDemo ? 'demo' : 'regular'
    commit(SET_USER_SEGMENT, segment)
    flagsmith.setTrait('userSegment', segment)

    if (typeof isDemo === 'undefined') {
      const warningMessage = 'Flagsmith: isDemo trait not found, defaulting userSegment to regular.'
      console.warn(warningMessage)
      reportToSentry(warningMessage, 'warning')
    }
  },

  /**
   * Manually refreshes feature flags from Flagsmith server
   *
   * This method is primarily used when we need to immediately update flag states
   * without waiting for the automatic 5-second polling interval. Use cases include:
   * - After user actions that might change their segment
   * - When immediate flag updates are required for critical features
   * - For testing flag changes in development
   * - When users manually request a refresh via UI
   */
  async [REFRESH_FLAGS]({ commit }) {
    await flagsmith.getFlags()

    const flagsToRefresh = [
      FLAG_CONSTANTS.RETAILER_AI,
      FLAG_CONSTANTS.UNIFIED_INVITE_RETAILER,
    ]
    flagsToRefresh.forEach(flagKey => {
      commit(UPDATE_FLAG, {
        flag: flagKey,
        value: flagsmith.hasFeature(flagKey),
      })
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
